import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import {
    ChipStatus,
    LayoutModule,
    NotificationsService,
    SharedUiModule,
} from '@pf/shared-ui';
import {
    ContentItem,
    CpwBackendService,
    CpwShipmentPackage,
    CpwShippingDetails,
    FedexDocumentTypes,
    FedExShipmentType,
    ServiceType,
} from '@cpw/domain';
import {
    PackingListDto,
    ShipmentDocumentsFacade,
    ShipmentDto,
    ShipmentFacade,
    SlotDto,
    SlotFacade,
} from '@pf/platform-wms/domain';
import { CarrierDto, CarrierFacade } from '@pf/platform-core/domain';
import {
    catchError,
    forkJoin,
    map,
    Observable,
    of,
    switchMap,
    take,
    tap,
} from 'rxjs';
import {
    DocumentDto,
    IAddressDto,
    NotificationType,
    ToastMessage,
    ToastType,
} from '@pf/shared-common';
import { ContactDto, ContactFacade } from '@pf/shared/util-platform';
import {
    buildPackingSlipLabels,
    LabelPrintService,
    LabelType,
    PrintLabelOptions,
    PrintOptions,
} from '@pf/shared/ui-platform';
import { asArraySafe } from '@pf/shared-utility';
import {
    BarcodeScanEvent,
    CpwScanInputComponent,
} from './cpw-scan-input.component';
import { CommonModule } from '@angular/common';
import { ShipmentStatus, ZoneType } from '@control-tower/platform-wms';

interface DocumentPrintQueue {
    labels: boolean;
    bol: boolean;
    commercialInvoice: boolean;
    packingSlips: boolean;
}

@Component({
    selector: 'cpw-shipping-labels',
    standalone: true,
    imports: [
        CommonModule,
        SharedUiModule,
        LayoutModule,
        CpwScanInputComponent,
    ],
    templateUrl: './cpw-shipping-labels.component.html',
    styleUrl: './cpw-shipping-labels.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwShippingLabelsComponent {
    private readonly elementRef = inject(ElementRef);
    private readonly labelPrintService = inject(LabelPrintService);
    private readonly shipmentDocumentsFacade = inject(ShipmentDocumentsFacade);
    private readonly notificationsService = inject(NotificationsService);
    private readonly shipmentFacade = inject(ShipmentFacade);
    private readonly cpwBackendService = inject(CpwBackendService);
    private readonly carrierFacade = inject(CarrierFacade);
    private readonly contactFacade = inject(ContactFacade);
    readonly slotFacade = inject(SlotFacade);

    title = input<string>('');
    disabled = input<boolean>(false);
    active = input<boolean>(true);
    isHotShot = input<boolean>(false);
    isWillCall = input<boolean>(false);
    allowManualTracking = input<boolean>(false);
    serviceType = input<ServiceType>();
    shipToContactId = input<string>('eb0c4fb4-0cff-41dd-e52b-08de8c2d2430');
    carrierScac = input<string | null>('FDEN');
    shipToAddress = input<IAddressDto | null | undefined>(null);
    installAtAddress = input<IAddressDto | null | undefined>(null);
    orderNumber = input<string>('');
    workOrderNumber = input<string>('');
    requestor = input<string | null | undefined>(null);
    packages = input<PackingListDto[]>([]);
    existingShipment = input<ShipmentDto | null>(null);

    shipmentCreated = output<ShipmentDto>();
    shippingCompleted = output<void>();

    generating = signal<boolean>(false);
    shipping = signal<boolean>(false);
    scannedDockSlot = signal<SlotDto | null>(null);
    carrier = signal<CarrierDto | null>(null);
    shipToContact = signal<ContactDto | null>(null);
    shipmentDocuments = signal<DocumentDto[] | null>(null);
    manualTrackingNumber = signal<string>('');
    showManualTracking = signal<boolean>(false);

    protected shipment = signal<ShipmentDto | null>(null);

    documentPrintQueue = signal < {};
    autoPrintPending = signal(false);
    private hasScrolledToConfirmShipment = false;

    isHazardous = computed(() => {
        return this.packages().some(p =>
            asArraySafe(p.packLines).some(i => !!i.bundle?.item?.hazardous)
        );
    });

    shipmentAddress = computed(() => {
        const address = this.shipToAddress();
        if (!address) {
            return null;
        }

        return {
            name: address.name ?? '',
            addressLine1: address.addressLine1,
            addressLine2: address.addressLine2 || '',
            city: address.city,
            geoZone: address.geoZone ?? '',
            postalCode: address.postalCode,
            country: address.country,
        };
    });

    readyToShip = computed(() => {
        const shipment = this.shipment();
        if (!shipment) {
            return false;
        }
        if (this.isHotShot() || this.isWillCall()) {
            return true;
        }
        return !!shipment.trackingNumber;
    });

    readonly useManualTracking = computed(
        () => this.allowManualTracking() && !!this.manualTrackingNumber()
    );

    readonly hasPallets = computed(() =>
        this.packages().some(p => !p.item?.id)
    );

    readonly ltlProcessingChip = computed<{
        status: ChipStatus;
        label: string;
    }>(() => {
        if (this.isHotShot()) {
            return {
                status: 'danger',
                label: 'Hot Shot Processing',
            };
        }
        if (this.isWillCall()) {
            return {
                status: 'danger',
                label: 'Will Call Processing',
            };
        }
        return {
            status: 'primary',
            label: 'Dock Processing',
        };
    });

    readonly shipmentPalletContainerIds = computed(() => {
        const ids = new Set<string>();
        for (const pkg of asArraySafe(this.shipment()?.packingLists)) {
            if (!pkg.item?.id) {
                for (const pl of asArraySafe(pkg.packLines)) {
                    const containerId = pl.inventoryBundle?.container?.id;
                    if (containerId) {
                        ids.add(containerId);
                    }
                }
            }
        }
        return Array.from(ids);
    });

    readonly canFinish = computed(() => {
        if (this.disabled()) return false;
        if (this.shipment()?.status === ShipmentStatus.Shipped) return false;
        if (!this.allDocumentsPrinted()) return false;
        if (this.isWillCall() || this.isHotShot()) return true;
        if (this.hasPallets() && !this.scannedDockSlot()) return false;
        return true;
    });

    readonly hasAnyPackages = computed(() => this.packages().length > 0);

    readonly shippingLabels = computed(() => {
        return this.getDocumentsByType(
            FedexDocumentTypes.Label,
            this.shipmentDocuments()
        );
    });

    readonly billOfLading = computed(() => {
        return (
            this.getDocumentsByType(
                FedexDocumentTypes.BillOfLading,
                this.shipmentDocuments()
            )?.[0] || null
        );
    });

    readonly commercialInvoice = computed(() => {
        return (
            this.getDocumentsByType(
                FedexDocumentTypes.CommercialInvoice,
                this.shipmentDocuments()
            )?.[0] || null
        );
    });

    requiredDocuments = computed<DocumentPrintQueue>(() => {
        return {
            labels: !!this.shippingLabels()?.length,
            bol: !!this.billOfLading(),
            commercialInvoice: !!this.commercialInvoice(),
            packingSlips: this.hasPackingSlips(),
        };
    });

    printedDocuments = signal<DocumentPrintQueue>({
        labels: false,
        bol: false,
        commercialInvoice: false,
        packingSlips: false,
    });

    readonly packingSlipLabels = computed<PrintLabelOptions[]>(() =>
        this.buildPackingSlipLabelOptions()
    );

    readonly hasPackingSlips = computed(
        () => this.packingSlipLabels().length > 0
    );

    allDocumentsPrinted = computed(() => {
        const requiredDocuments = this.requiredDocuments();
        const printedDocuments = this.printedDocuments();
        return (
            this.readyToShip() &&
            Object.keys(requiredDocuments).every(key => {
                return (
                    requiredDocuments[key as keyof DocumentPrintQueue] ===
                    printedDocuments[key as keyof DocumentPrintQueue]
                );
            })
        );
    });

    readonly useFedEx = computed(
        () =>
            !this.isHotShot() &&
            !this.isWillCall() &&
            !this.showManualTracking()
    );

    readonly shipmentDetails = computed<CpwShippingDetails | null>(() => {
        const shipment = this.shipment();
        const shipTo = this.shipmentAddress();
        const packages = this.packages();
        const serviceType = this.serviceType();
        const carrier = this.carrier();
        const labels = this.mapPackingListsToShippingLabels(packages);
        const speed = this.useFedEx()
            ? serviceType && ServiceType[serviceType]
            : null;
        const service = this.useFedEx()
            ? carrier?.carrierName
            : this.isWillCall()
              ? 'Will call pickup'
              : this.isHotShot()
                ? 'Circle Logistics'
                : null;

        return {
            id: shipment?.id,
            shipTo,
            packages: labels,
            service,
            speed,
            trackingNumber: shipment?.trackingNumber,
            requestor: this.requestor() ?? null,
        };
    });

    readonly hasPackingListCreated = computed(() => {
        return (
            this.packages()
                .map(p => p.id)
                .filter(id => !!id).length > 0
        );
    });

    readonly canConfirmShipment = computed(() => {
        if (this.disabled()) {
            return false;
        }
        if (this.showManualTracking() && !this.manualTrackingNumber()) {
            return false;
        }

        return this.hasPackingListCreated();
    });

    constructor() {
        effect(() => {
            const carrierScac = this.carrierScac();
            if (carrierScac) {
                this.carrierFacade
                    .getByScac$(carrierScac)
                    .subscribe(carrier => {
                        this.carrier.set(carrier);
                    });
            }
        });
        effect(() => {
            this.contactFacade
                .getById$(this.shipToContactId())
                .subscribe(contact => {
                    this.shipToContact.set(contact);
                });
        });
        effect(() => {
            const existingShipment = this.existingShipment();
            if (existingShipment) {
                this.shipment.set(existingShipment);
            }
        });
        effect(() => {
            const active = this.active();
            const hasPackages = this.hasAnyPackages();
            const ready = this.readyToShip();
            const details = this.shipmentDetails();

            if (
                active &&
                hasPackages &&
                details &&
                !ready &&
                !this.hasScrolledToConfirmShipment
            ) {
                this.hasScrolledToConfirmShipment = true;
                setTimeout(() => {
                    this.elementRef.nativeElement
                        .querySelector('#confirm-shipment')
                        ?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        });
                }, 0);
            }
        });
        effect(() => {
            const shipment = this.shipment();
            if (!shipment) {
                this.shipmentDocuments.set(null);
                this.printedDocuments.set({
                    labels: false,
                    bol: false,
                    commercialInvoice: false,
                    packingSlips: false,
                });
                this.scannedDockSlot.set(null);

                // Check the packing lists to see if we have a shipment
                this.lookupShipmentFromPackingLists();
                return;
            }
            this.loadShipmentDocuments(shipment.id);
        });
        effect(() => {
            const labels = this.shippingLabels();
            const pending = this.autoPrintPending();
            const useManualTracking = this.useManualTracking();
            const hasPackingSlips = this.hasPackingSlips();

            if (!pending) {
                return;
            }

            if (labels?.length) {
                this.autoPrintPending.set(false);
                this.onPrintLabels();
                return;
            }

            if (useManualTracking && hasPackingSlips) {
                this.autoPrintPending.set(false);
                this.onPrintPackingSlips();
                return;
            }

            if ((this.isHotShot() || this.isWillCall()) && hasPackingSlips) {
                this.autoPrintPending.set(false);
                this.onPrintPackingSlips();
            }
        });
    }

    generateShippingLabels() {
        if (this.disabled() || this.generating()) {
            return;
        }
        this.generating.set(true);

        const validation = this.validateShipmentInputs();
        if (!validation.valid) {
            this.generating.set(false);
            this.showValidationError(validation.missingFields);
            return;
        }

        const hadExistingShipment = !!this.shipment();

        this.getOrCreateShipment$()
            .pipe(
                tap(shipment => {
                    if (!hadExistingShipment) {
                        this.shipmentCreated.emit(shipment);
                    }
                }),
                switchMap(shipment => this.processTracking$(shipment)),
                tap(result => this.shipment.set(result)),
                tap(() => this.onShipmentGenerated()),
                catchError(error => this.handleGenerationError(error))
            )
            .subscribe();
    }

    private validateShipmentInputs(): {
        valid: boolean;
        missingFields: string[];
    } {
        const config = this.isWillCall()
            ? {
                  requireCarrier: false,
                  requireShipTo: false,
                  requireServiceType: false,
                  requireShipToContact: false,
              }
            : this.isHotShot()
              ? {
                    requireCarrier: false,
                    requireShipTo: true,
                    requireServiceType: false,
                    requireShipToContact: true,
                }
              : {
                    requireCarrier: true,
                    requireShipTo: true,
                    requireServiceType: true,
                    requireShipToContact: true,
                };
        const missingFields = this.validateRequiredFields(config);
        return { valid: missingFields.length === 0, missingFields };
    }

    private validateRequiredFields(config: {
        requireCarrier: boolean;
        requireShipTo: boolean;
        requireServiceType: boolean;
        requireShipToContact: boolean;
    }): string[] {
        const missingFields: string[] = [];
        const packingListIds = this.packages().map(p => p.id);
        if (!packingListIds?.length) missingFields.push('packing lists');
        if (config.requireCarrier && !this.carrier()?.id)
            missingFields.push('carrier');
        if (config.requireShipTo && !this.shipmentAddress())
            missingFields.push('ship to address');
        if (config.requireServiceType && !this.serviceType())
            missingFields.push('service type');
        if (config.requireShipToContact && !this.shipToContact())
            missingFields.push('ship to contact');
        return missingFields;
    }

    private showValidationError(missingFields: string[]): void {
        this.notificationsService.create({
            type: NotificationType.ToastMessage,
            toastType: ToastType.warning,
            message: `Cannot generate shipment: ${missingFields.join(', ')} ${missingFields.length > 1 ? 'are' : 'is'} required.`,
            duration: 2000,
        } as ToastMessage);
    }

    private getOrCreateShipment$(): Observable<ShipmentDto> {
        const existingShipment = this.shipment();
        if (existingShipment) {
            return of(existingShipment);
        }

        if (this.isWillCall()) {
            return this.createWillCallShipment$();
        }
        if (this.isHotShot()) {
            return this.createHotShotShipment$();
        }
        return this.createRegularShipment$();
    }

    private createWillCallShipment$(): Observable<ShipmentDto> {
        return this.shipmentFacade.createShipmentFromPackingLists(
            this.packages().map(p => p.id),
            this.carrier()?.id ?? null,
            this.shipmentAddress(),
            this.shipToContact(),
            this.manualTrackingNumber(),
            null
        );
    }

    private createHotShotShipment$(): Observable<ShipmentDto> {
        const shipTo = this.shipmentAddress();
        const shipToContact = this.shipToContact();
        if (!shipTo || !shipToContact) {
            throw new Error(
                'Ship to address and contact are required for hot shot shipments'
            );
        }
        const externalReferenceId =
            this.orderNumber() && this.workOrderNumber()
                ? `${this.orderNumber()}-${this.workOrderNumber()}`
                : null;
        return this.shipmentFacade.createShipmentFromPackingLists(
            this.packages().map(p => p.id),
            this.carrier()?.id ?? null,
            shipTo,
            shipToContact,
            this.manualTrackingNumber(),
            externalReferenceId
        );
    }

    private createRegularShipment$(): Observable<ShipmentDto> {
        const carrier = this.carrier();
        const shipTo = this.shipmentAddress();
        const shipToContact = this.shipToContact();
        if (!carrier?.id || !shipTo || !shipToContact) {
            throw new Error(
                'Carrier, ship to address, and contact are required for regular shipments'
            );
        }
        return this.shipmentFacade.createShipmentFromPackingLists(
            this.packages().map(p => p.id),
            carrier.id,
            shipTo,
            shipToContact,
            this.manualTrackingNumber(),
            null
        );
    }

    private processTracking$(shipment: ShipmentDto): Observable<ShipmentDto> {
        if (this.useManualTracking()) {
            return this.processManualTracking$(shipment);
        }
        if (this.useFedEx()) {
            return this.processFedExShipment$(shipment);
        }
        return of(shipment);
    }

    private processManualTracking$(
        shipment: ShipmentDto
    ): Observable<ShipmentDto> {
        return this.shipmentFacade.save$({
            ...shipment,
            trackingNumber: this.manualTrackingNumber(),
        } as ShipmentDto);
    }

    private processFedExShipment$(
        shipment: ShipmentDto
    ): Observable<ShipmentDto> {
        const serviceType = this.serviceType();
        if (!serviceType) {
            throw new Error('Service type is required for FedEx shipments');
        }
        const shipmentType = this.hasPallets()
            ? FedExShipmentType.Ltl
            : FedExShipmentType.Parcel;
        return this.cpwBackendService
            .createFedExShipment$({
                shipmentId: shipment.id,
                shipmentType,
                serviceType,
            })
            .pipe(
                map(response => response.trackingNumber),
                map(
                    trackingNumber =>
                        new ShipmentDto({
                            ...shipment,
                            trackingNumber,
                        })
                )
            );
    }

    private onShipmentGenerated(): void {
        this.generating.set(false);
        this.showSuccessNotification();
        this.autoPrintPending.set(true);
        this.scrollToFinishButton();
    }

    private handleGenerationError(error: unknown) {
        this.generating.set(false);
        console.error('Error generating shipping labels:', error);
        this.showErrorNotification(error);
        return of(null);
    }

    protected onPrintLabels() {
        if (this.disabled()) {
            return;
        }
        const shipmentId = this.shipment()?.id;
        const labels = this.shippingLabels();
        if (!shipmentId || !labels) {
            return;
        }

        forkJoin(
            labels.map(label =>
                this.shipmentDocumentsFacade.readContents$(shipmentId, label.id)
            )
        )
            .pipe(
                map(contents => {
                    const shippingLabelOptions: PrintLabelOptions[] =
                        contents.map((content, index) => ({
                            barcode: `Shipping Label ${index + 1}`,
                            tags: ['Shipping'],
                            zpl: content,
                        }));
                    const combinedLabels = [
                        ...shippingLabelOptions,
                        ...this.packingSlipLabels(),
                    ];
                    return {
                        labelType: LabelType.ShippingLabel,
                        autoPrint: true,
                        labels: combinedLabels,
                        widthInches: 4,
                        heightInches: 6,
                    } as PrintOptions;
                })
            )
            .subscribe(options => {
                this.labelPrintService.printLabels(options, () => {
                    this.updatePrintedDocuments('labels');
                    if (this.hasPackingSlips()) {
                        this.updatePrintedDocuments('packingSlips');
                    }
                });
            });
    }

    protected onPrintPackingSlips() {
        if (this.disabled()) return;
        const slips = this.packingSlipLabels();
        if (!slips.length) return;

        const options: PrintOptions = {
            labelType: LabelType.PackingSlip,
            autoPrint: true,
            labels: slips,
            widthInches: 4,
            heightInches: 6,
        };
        this.labelPrintService.printLabels(options, () =>
            this.updatePrintedDocuments('packingSlips')
        );
    }

    protected onPrintBol() {
        this.printDocumentIfExists(this.billOfLading(), 'bol');
    }

    protected onPrintCommercialInvoice() {
        this.printDocumentIfExists(
            this.commercialInvoice(),
            'commercialInvoice'
        );
    }

    private printDocumentIfExists(
        document: DocumentDto | null,
        documentKey: keyof DocumentPrintQueue
    ) {
        if (this.disabled() || !document) return;
        this.printDocumentNative(document.id, () =>
            this.updatePrintedDocuments(documentKey)
        );
    }

    onManualTrackingEntered($event: BarcodeScanEvent) {
        if (this.disabled() || this.generating()) {
            $event.processed.next(false);
            return;
        }
        const trackingNumber = $event.scanInput?.trim();
        if (!trackingNumber) {
            $event.processed.next(false);
            return;
        }
        this.manualTrackingNumber.set(trackingNumber);
        $event.processed.next(true);
        this.generateShippingLabels();
    }

    onDockSlotScanned($event: BarcodeScanEvent) {
        if (this.disabled()) {
            $event.processed.next(false);
            return;
        }
        const slot = $event.entity as SlotDto;
        const isShipping = slot?.zone?.zoneType === ZoneType.Shipping;
        if (isShipping) {
            this.scannedDockSlot.set(slot);
        }
        $event.processed.next(isShipping);
    }

    protected completeShipment() {
        if (this.disabled()) {
            return;
        }
        const shipment = this.shipment();
        if (!shipment) {
            throw new Error('Shipment not found');
        }
        this.shipping.set(true);

        this.shipmentFacade
            .ship$(shipment.id)
            .pipe(
                tap(() => {
                    this.shippingCompleted.emit();
                    this.shipment.update(s => {
                        if (s) {
                            s.status = 'Shipped';
                        }
                        return s;
                    });
                    this.shipping.set(false);
                }),
                map(() => true),
                catchError(error => {
                    this.shipping.set(false);
                    this.notificationsService.create({
                        type: NotificationType.ToastMessage,
                        toastType: ToastType.error,
                        message: `Failed to complete shipment. ${error?.message || 'Please try again.'}`,
                        duration: 2000,
                    } as ToastMessage);
                    return of(false);
                })
            )
            .subscribe();
    }

    private mapPackingListsToShippingLabels(
        packages: PackingListDto[]
    ): CpwShipmentPackage[] {
        return packages.map(p => this.mapPackingListToShippingLabel(p));
    }

    private mapPackingListToShippingLabel(
        p: PackingListDto
    ): CpwShipmentPackage {
        const itemsByDescription = this.buildItemsByDescription(p.packLines);
        const totalQuantity = this.sumQuantities(itemsByDescription);
        const isPallet = !!p.dimensions;

        return {
            contentDescription: undefined,
            contentItems: this.toContentItems(itemsByDescription),
            totalQuantity,
            weight: p.weight?.pounds ? `${p.weight.pounds} lbs` : null,
            box: isPallet ? null : this.getBoxDescription(itemsByDescription),
            dimensions: this.formatDimensions(p.dimensions),
            label: null,
            value: null,
        } as CpwShipmentPackage;
    }

    private buildItemsByDescription(
        packLines: PackingListDto['packLines']
    ): Map<string, number> {
        const itemsByDescription = new Map<string, number>();

        asArraySafe(packLines).forEach(pl => {
            const itemDescription = pl.bundle?.item?.name;
            if (itemDescription && pl.packedQuantity) {
                const currentQty = itemsByDescription.get(itemDescription) ?? 0;
                itemsByDescription.set(
                    itemDescription,
                    currentQty + pl.packedQuantity
                );
            }
        });

        return itemsByDescription;
    }

    private sumQuantities(itemsByDescription: Map<string, number>): number {
        return Array.from(itemsByDescription.values()).reduce(
            (sum, qty) => sum + qty,
            0
        );
    }

    private toContentItems(
        itemsByDescription: Map<string, number>
    ): ContentItem[] {
        return Array.from(itemsByDescription.entries()).map(
            ([description, qty]) => ({ description, quantity: qty })
        );
    }

    private getBoxDescription(
        itemsByDescription: Map<string, number>
    ): string | null {
        const size = itemsByDescription.size;
        if (size === 0) return null;
        if (size === 1) {
            const [description] = Array.from(itemsByDescription.entries())[0];
            return description;
        }
        return 'Mixed Items';
    }

    private formatDimensions(
        dimensions: PackingListDto['dimensions']
    ): string | null {
        const { length, width, height } = dimensions ?? {};
        if (!length?.inches || !width?.inches || !height?.inches) return null;
        return `${length.inches}x${width.inches}x${height.inches} in`;
    }

    private loadShipmentDocuments(shipmentId: string) {
        this.shipmentDocumentsFacade.all$(shipmentId).subscribe(documents => {
            this.shipmentDocuments.set(documents);
        });
    }

    private getDocumentsByType(
        type: FedexDocumentTypes,
        documents?: DocumentDto[] | null
    ) {
        const matches = asArraySafe(documents).filter(
            d => d.description === type
        );
        return matches.length > 0 ? matches : null;
    }

    private buildPackingSlipLabelOptions(): PrintLabelOptions[] {
        return buildPackingSlipLabels({
            packingLists: this.packages(),
            trackingNumber: this.shipment()?.trackingNumber,
            orderNumber: this.orderNumber(),
            shipTo: this.shipToAddress(),
            installAt: this.installAtAddress(),
            requestor: this.requestor() ?? null,
        });
    }

    private updatePrintedDocuments(documentTypeKey: keyof DocumentPrintQueue) {
        this.printedDocuments.update(documents => ({
            ...documents,
            [documentTypeKey]: true,
        }));
    }

    private lookupShipmentFromPackingLists() {
        const packages = this.packages();
        if (!packages.length) return;

        // Find first package with a shipmentId
        const packageWithShipment = packages.find(p => p.shipmentId);
        if (!packageWithShipment?.shipmentId) return;

        // Look up the shipment
        this.shipmentFacade
            .getById$(packageWithShipment.shipmentId)
            .pipe(take(1))
            .subscribe(shipment => {
                if (shipment) {
                    this.shipment.set(shipment);
                }
            });
    }

    private scrollToFinishButton() {
        setTimeout(() => {
            this.elementRef.nativeElement
                .querySelector('#finish-button')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 0);
    }

    private showSuccessNotification() {
        const message = this.getSuccessMessage();
        this.notificationsService.create({
            type: NotificationType.ToastMessage,
            toastType: ToastType.success,
            message,
            duration: 2000,
        } as ToastMessage);
    }

    private getSuccessMessage(): string {
        if (this.useManualTracking())
            return 'Tracking number saved. Shipment is ready.';
        if (this.useFedEx())
            return 'Shipping labels have been generated and are ready for printing.';
        return 'Shipment is ready for external processing.';
    }

    private showErrorNotification(error: unknown) {
        const errorMessage = this.extractFedExErrors(error);
        const baseMessage = this.getErrorBaseMessage();
        this.notificationsService.create({
            type: NotificationType.ToastMessage,
            toastType: ToastType.error,
            message: `${baseMessage} ${errorMessage}`,
            duration: 2000,
        } as ToastMessage);
    }

    private getErrorBaseMessage(): string {
        if (this.useManualTracking()) return 'Failed to save tracking number.';
        if (this.useFedEx()) return 'Failed to generate shipping labels.';
        return 'Failed to create shipment.';
    }

    private extractFedExErrors(error: unknown): string {
        const err = error as {
            error?: {
                errors?: { property: string; error: string }[];
                detail?: string;
                title?: string;
            };
            message?: string;
        };
        const body = err?.error;
        if (body?.errors?.length) {
            return body.errors.map(e => `${e.property}: ${e.error}`).join('; ');
        }
        return (
            body?.detail ?? body?.title ?? err?.message ?? 'Please try again.'
        );
    }

    private printDocumentNative(documentId: string, onPrinted: () => void) {
        const shipmentId = this.shipment()?.id;
        if (!shipmentId) return;

        this.shipmentDocumentsFacade
            .previewUrl$(shipmentId, documentId)
            .subscribe(url => {
                const iframe = document.createElement('iframe');
                iframe.style.display = 'none';
                iframe.src = url;
                document.body.appendChild(iframe);

                iframe.onload = () => {
                    setTimeout(() => {
                        iframe.contentWindow?.focus();
                        iframe.contentWindow?.print();
                        onPrinted();
                    }, 500);
                    iframe.contentWindow?.addEventListener('afterprint', () => {
                        document.body.removeChild(iframe);
                        URL.revokeObjectURL(url);
                    });
                };
            });
    }
}
